import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import { drawerManagerToken } from "@apps/rewards/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsDrawerWidgetComponent from "./RewardsDrawerWidget.svelte";
import type { DrawerWidgetConfig } from "./RewardsDrawerWidgetConfig";

@injectable()
class RewardsDrawerWidget implements WidgetInterface {
    constructor(
        private readonly config: DrawerWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsDrawerWidget is initialized", config);
    }

    async init(): Promise<this> {
        const widgetName = REWARDS_WIDGET_NAMES.REWARDS_DRAWER_WIDGET;
        const widgetNamespace = TRANSLATION_NAMESPACES.criticalDrawerWidget;

        const diResolverService = new RewardsDiResolverService();
        const {
            storefrontValuesService,
            i18nModule,
            dynamicImportModule,
            customizationManager,
            drawerManager,
        } = diResolverService.resolveAll({
            storefrontValuesService: RewardsStorefrontValuesService,
            i18nModule: I18nModule,
            dynamicImportModule: DynamicImportModule,
            customizationManager: CustomizationManager,
            drawerManager: drawerManagerToken,
        });

        const { i18n, i18nCriticalLoaderFactory } = i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nCriticalLoaderFactory.getLoader(widgetNamespace)
        );

        const placeholder = document.createElement("div");
        placeholder.classList.add("gw-rd-drawer-widget-placeholder");
        placeholder.style.display = "block";
        this.config.target.appendChild(placeholder);

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManagerConfig = {
            widgetName,
            placeholder,
            shadowRoot,
        };
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsDrawerWidgetComponent({
            target: shadowRoot,
            props: {
                storefrontValuesService,
                i18n,
                dynamicImportManager:
                    dynamicImportModule.exports.dynamicImportManager,
                logger: this.logger,
                drawerManager,
                customizationManager,
                widgetName,
                widgetNamespace,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default RewardsDrawerWidget;
